import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";
import { useAccessToken } from "../useAccessToken";

export const lmsKeys = {
  lms: () => ["lms"] as const,
  course: (_id: string) => [...lmsKeys.lms(), "course", _id] as const,
  courses: () => [...lmsKeys.lms(), "courses"] as const,
  allEnrollments: () => [...lmsKeys.lms(), "enrollments"] as const,
  courseEnrollments: (courseId: string) =>
    [...lmsKeys.lms(), "enrollments", "course", courseId] as const,
  userEnrollments: (userId: string) =>
    [...lmsKeys.lms(), "enrollments", "user", userId] as const,
  lessonEvents: (lessonId: string) =>
    [...lmsKeys.lms(), "events", "lesson", lessonId] as const,
  lesson: (lessonId: string) => [...lmsKeys.lms(), "lesson", lessonId] as const,
  lessons: () => [...lmsKeys.lms(), "lessons"] as const,
};

export type CourseData = {
  _id: string;
  name: string;
  description: string;
  lessons: LessonData[];
  enrollments: CourseEnrollmentData[];
  lessonEvents: LessonEventData[];
  instanceId: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type LessonData = {
  _id: string;
  title: string;
  description: string;
  type: "video";
  summary: string;
  videoUrl: string;
  instanceId: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type CourseEnrollmentData = {
  _id: string;
  courseId: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  customer?: {
    _id: string;
    name: string;
  };
  lessons: {
    name: string;
    isCompleted: boolean;
    timeViewed: number;
    totalTime: number;
  }[];
  instanceId: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type LessonEventData = {
  _id: string;
  lessonId: string;
  eventType: "viewed";
  metadata: {
    timeViewed: number;
  };
  user: {
    _id: string;
    name: string;
    email: string;
  };
  taskId: string;
  courseId: string;
  instanceId: string;
  createdAt: string;
  updatedAt: string;
};

export type ReducedCourseData = {
  _id: string;
  name: string;
  description: string;
  lessonIds: string[];
  instanceId: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  noOfEnrollments: number;
  createdAt: string;
  updatedAt: string;
};

export const useGetCourse = (_id: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.course(_id),
    async (): Promise<CourseData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/course`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            _id,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetCourses = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.courses(),
    async (): Promise<ReducedCourseData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/courses`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetCourseEnrollments = (courseId: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.courseEnrollments(courseId),
    async (): Promise<CourseEnrollmentData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/courseEnrollments`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            courseId,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetAllEnrollments = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.allEnrollments(),
    async (): Promise<CourseEnrollmentData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/enrollments`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            // userId: app.currentUser.customData._id,
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetUserEnrollments = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.userEnrollments(app.currentUser.customData._id),
    async (): Promise<CourseEnrollmentData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/userEnrollments`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            userId: app.currentUser.customData._id,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetLessonEvents = (lessonId: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.lessonEvents(lessonId),
    async (): Promise<LessonEventData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/lessonEvents`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            lessonId,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetLesson = (_id: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.lesson(_id),
    async (): Promise<LessonData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/lesson`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            _id,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetLessons = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    lmsKeys.lessons(),
    async (): Promise<LessonData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/lessons`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useCreateCourse = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (course: {
      name: string;
      description: string;
      lessonIds: string[];
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/course`,
        {
          instanceId: app.currentUser.customData.instanceId.$oid,
          ...course,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating course", "error");
    },
    onSuccess: (data) => {
      navigate(`/lms/courses/${data.id}`);
      queryClient.invalidateQueries(lmsKeys.courses());
    },
  });
};

export const useUpdateCourse = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (course: {
      _id: string;
      name?: string;
      description?: string;
      lessonIds?: string[];
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateCourse`,
        {
          ...course,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating course", "error");
    },
    onSuccess: (_, { _id }) => {
      queryClient.invalidateQueries(lmsKeys.courses());
      queryClient.invalidateQueries(lmsKeys.course(_id));
    },
  });
};

export const useCreateLesson = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (lesson: {
      title: string;
      description: string;
      summary: string;
      videoUrl: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/lesson`,
        {
          instanceId: app.currentUser.customData.instanceId.$oid,
          ...lesson,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating lesson", "error");
    },
    onSuccess: (data) => {
      navigate(`/lms/lessons/${data.id}`);
      queryClient.invalidateQueries(lmsKeys.lessons());
    },
  });
};

export const useUpdateLesson = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (lesson: {
      _id: string;
      title?: string;
      description?: string;
      summary?: string;
      videoUrl?: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateLesson`,
        {
          ...lesson,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating lesson", "error");
    },
    onSuccess: (_, { _id }) => {
      queryClient.invalidateQueries(lmsKeys.lessons());
      queryClient.invalidateQueries(lmsKeys.lesson(_id));
    },
  });
};

export const useEnrollUserInCourse = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (enrollment: {
      courseId: string;
      userId: string;
      customerId?: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/enrollUserInCourse`,
        {
          ...enrollment,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error enrolling user in course", "error");
    },
    onSuccess: (data, { courseId, userId }) => {
      queryClient.invalidateQueries(lmsKeys.courseEnrollments(courseId));
      queryClient.invalidateQueries(lmsKeys.userEnrollments(userId));
      queryClient.invalidateQueries(lmsKeys.allEnrollments());
    },
  });
};

export const useRecordLessonEvent = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (event: {
      lessonId: string;
      eventType: "viewed" | "completed";
      taskId?: string;
      courseId?: string;
      metadata?: {
        timeViewed: number;
      };
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/recordLessonEvent`,
        {
          ...event,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error recording lesson event", "error");
    },
    onSuccess: (_, { lessonId }) => {
      queryClient.invalidateQueries(lmsKeys.lessonEvents(lessonId));
    },
  });
};

// export const useDeleteSpace = () => {
//   const app = useRealmApp();
//   const snackbarCtx = useSnackBar();
//   const queryClient = useQueryClient();
//   const navigate = useNavigate();
//   const getValidAccessToken = useAccessToken();

//   return useMutation({
//     mutationFn: async (space: {
//       _id: string;
//     }): Promise<{ message: string; id: string }> => {
//       const accessToken = await getValidAccessToken();
//       const res = await axios.delete(
//         `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/space`,
//         {
//           headers: {
//             Accept: "application/json",
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//           params: space,
//         }
//       );
//       return res.data;
//     },
//     onError: () => {
//       snackbarCtx.showSnackbar("Error deleting space", "error");
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries(["spaces"]);
//       navigate(`/`);
//       // if (data.id && variables.sharedSpaceId) {
//       //   queryClient.invalidateQueries(
//       //     phaseKeys.phases(variables.sharedSpaceId)
//       //   );
//       // }
//       // snackbarCtx.showSnackbar("Successfully duplicated journey template");
//     },
//   });
// };
